import type { ComponentType } from 'react';
import type { IconName } from '@tutero/ui';

import type { SectionId } from './sections';

/** A grant's shape, `<tool>:<action>` (`services/auth/src/staff.ts` › `isPermission`). */
export type Permission = `${string}:${string}`;

/**
 * What every tool declares, page or link (`admin-is-an-app` D3,
 * `manifests-draw-the-nav` D2, D3): what the shell needs to list it, route to it and
 * gate it, and nothing about how it works. A tool is its directory under
 * `src/tools/<id>/`, with this as `tool.ts`; adding one touches no other tool and not
 * the shell.
 */
interface Declared {
  /** Its directory's name, and its URL: `/<id>`. */
  id: string;
  title: string;
  /** One line in the nav and on Home: what it is for. */
  summary: string;
  /** The nav's heading it sits under, one the shell declares (`sections.ts`). */
  section: SectionId;
  /** Its place among that section's tools; sparse (10, 20, 30), ties falling to title. */
  order: number;
  /** Its glyph in the nav, a Material ligature `@tutero/ui` draws. */
  icon: IconName;
  /** Its GitHub handle (`.github/CODEOWNERS`), who answers for it. */
  owner: `@${string}`;
  /** The grant that opens it; its backend refuses its operations without it too. */
  permission: Permission;
  /** Further grants a manager may give for it, beyond opening it. */
  grants?: readonly Permission[];
}

/** A tool with a page of its own at `/<id>`. An embed is one of these, drawing an `iframe`. */
export interface PageTool extends Declared {
  kind?: 'page';
  /**
   * Its page, loaded only once the session holds `permission`. It owns everything under
   * `/<id>/`, and reads the rest of the path with `useToolPath`.
   */
  routes: () => Promise<{ default: ComponentType }>;
}

/** A row that opens an outside address in a tab, with no route of its own (D4). */
export interface LinkTool extends Declared {
  kind: 'link';
  href: string;
}

export type Tool = PageTool | LinkTool;

/** A tool's manifest, checked by the compiler. */
export function defineTool<T extends Tool>(tool: T): T {
  return tool;
}

export function isLink(tool: Tool): tool is LinkTool {
  return tool.kind === 'link';
}
